import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useSeoMeta } from '@unhead/react';
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  FileEdit,
  GripVertical,
  ImagePlus,
  Loader2,
  Maximize2,
  Plus,
  Save,
  Send,
  Trash2,
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { AsciiDocEditor } from '@/components/AsciiDocEditor';
import { FocusModeView } from '@/components/FocusModeView';
import { FocusSectionNav } from '@/components/FocusSectionNav';
import { SaveStatusBadge, type SaveState } from '@/components/SaveStatusBadge';
import { DjotEditor } from '@/components/DjotEditor';
import { EditorFormatSelect } from '@/components/EditorFormatSelect';
import { editorPlaceholder } from '@/lib/editorFormat';
import { EditorViewToggle, type EditorViewMode } from '@/components/EditorViewToggle';
import { MediaPickerDialog } from '@/components/media/MediaPickerDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePublicationDrafts } from '@/hooks/usePublicationDrafts';
import { isPublicationDraftEmpty } from '@/lib/publicationDrafts';
import { useResolvePendingMedia } from '@/hooks/useMediaLibrary';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { usePublicationIndex, usePublicationSections } from '@/hooks/usePublications';
import { insertBlockAtCursor } from '@/lib/djotEditing';
import { computeEditorStats } from '@/lib/editorStats';
import { detectContentFormat, isContentFormat, type ContentFormat } from '@/lib/contentFormat';
import { findPendingMediaHashes, resolvePendingMedia } from '@/lib/mediaMarkup';
import { normalizeWikiTitle, prettifySlug } from '@/lib/wikiSlug';
import {
  KNOWN_IDENTIFIER_TYPES,
  PUBLICATION_CONTENT_KIND,
  PUBLICATION_INDEX_KIND,
  getPublicationAuthors,
  getPublicationIdentifiers,
  getPublicationTopics,
  publicationImage,
  publicationPublishedBy,
  publicationPublishedOn,
  publicationSource,
  publicationSummary,
  publicationTitle,
  publicationType,
  publicationVersion,
  sectionTitle,
  type PublicationType,
} from '@/lib/nkbip01';

interface DraftSection {
  /** Stable client-side key for React list rendering / reordering; not persisted. */
  key: string;
  d: string;
  title: string;
  content: string;
  /** The format the section gets edited in. Defaults to `detectContentFormat` when a section is loaded, or `undefined` for a brand-new one. */
  format: ContentFormat | undefined;
  /** True once this section has been published at least once (so we know whether to keep its `d` tag stable). */
  published: boolean;
}

interface DraftAuthor {
  key: string;
  name: string;
  role: string;
}

interface DraftIdentifier {
  key: string;
  type: string;
  value: string;
}

const PUBLICATION_TYPES: PublicationType[] = ['book', 'illustrated', 'magazine', 'documentation', 'academic', 'blog'];

let keyCounter = 0;
function nextKey(): string {
  keyCounter += 1;
  return `draft-${keyCounter}`;
}

export function PublicationEditPage() {
  const { d: rawD } = useParams<{ d?: string }>();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { mutateAsync: uploadFile, isPending: isUploadingImage } = useUploadFile();
  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const d = rawD ? decodeURIComponent(rawD) : undefined;
  const isEditingExisting = Boolean(d);

  const { data: existingIndex, isLoading: indexLoading } = usePublicationIndex(user?.pubkey, d);
  const { data: existingSections, isLoading: sectionsLoading } = usePublicationSections(existingIndex);

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [type, setType] = useState<PublicationType>('book');
  const [image, setImage] = useState('');
  const [version, setVersion] = useState('');
  const [publishedOn, setPublishedOn] = useState('');
  const [publishedBy, setPublishedBy] = useState('');
  const [source, setSource] = useState('');
  const [topics, setTopics] = useState('');
  const [authors, setAuthors] = useState<DraftAuthor[]>([]);
  const [identifiers, setIdentifiers] = useState<DraftIdentifier[]>([]);
  const [sections, setSections] = useState<DraftSection[]>([]);
  const [initialized, setInitialized] = useState(!isEditingExisting);
  const [isPublishing, setIsPublishing] = useState(false);
  const [activeSectionKey, setActiveSectionKey] = useState<string | undefined>();
  const [mediaOpen, setMediaOpen] = useState(false);
  const [viewMode, setViewMode] = useState<EditorViewMode | undefined>();
  /** Fullscreen (focus) mode: section editor only. */
  const [focusMode, setFocusMode] = useState(false);
  const resolveMedia = useResolvePendingMedia();
  const drafts = usePublicationDrafts();
  const [loadedFrom, setLoadedFrom] = useState<string | null>(null);
  /** Tracks an in-progress draft save so the badge can distinguish it from plain "dirty". */
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  /** The editor state at the last durable save — the reference point for "unsaved changes". */
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);

  // Resume a publication draft by ?draft=<id> — same semantics as the
  // article editor's draft: the local draft is the source of truth,
  // relay prefill is fully skipped when it's being resumed. Declared
  // early (rather than after the early-return guards below) because the
  // resume/prefill effects below reference it in their dependency arrays.
  const draftId = searchParams.get('draft') ?? undefined;
  const draft = drafts.get(draftId);

  const finalD = normalizeWikiTitle(title) || d || '';
  const activeSection = sections.find((s) => s.key === activeSectionKey);
  const stats = activeSection ? computeEditorStats(activeSection.content) : { lines: 0, words: 0, charsWithMarkup: 0, charsWithoutMarkup: 0 };

  /**
   * A serialized snapshot of everything a draft persists.
   *
   * A publication has far too many fields (plus three variable-length
   * lists) to compare field-by-field the way the article editor does, and
   * a missed field would silently mean "no unsaved changes" for work that
   * really is unsaved. Serializing exactly the shape `drafts.save`
   * persists keeps the two in lockstep by construction — and section
   * `key`s are excluded because they're render-only bookkeeping that
   * changes on reorder without changing the document.
   */
  const snapshot = JSON.stringify({
    title,
    summary,
    type,
    image,
    version,
    publishedOn,
    publishedBy,
    source,
    topics,
    authors: authors.map((a) => [a.name, a.role]),
    identifiers: identifiers.map((i) => [i.type, i.value]),
    sections: sections.map((s) => [s.d, s.title, s.content, s.format ?? '']),
  });

  const dirty = savedSnapshot !== null && snapshot !== savedSnapshot;

  // Drives the focus-mode badge. `idle` hides it (nothing worth
  // reporting); `dirty` invites a click to save a *draft*; `saved` once
  // there's nothing pending. The badge never claims to publish — a draft
  // is local-only by design.
  const hasContent = Boolean(title.trim() || sections.some((s) => s.content.trim()));
  const saveState: SaveState = isSavingDraft ? 'saving' : dirty ? 'dirty' : hasContent ? 'saved' : 'idle';

  useSeoMeta({
    title: isEditingExisting ? `Edit ${title || prettifySlug(d ?? '')} — Wikistar` : 'New publication — Wikistar',
  });

  // Resume a draft on first mount when there's a `?draft=<id>`, seeded
  // synchronously instead of from the relays. Skips relay prefill
  // completely since the local draft IS the source of truth.
  //
  // A stale id — a draft deleted here, or created in another browser —
  // is reported and the param stripped, so normal relay prefill (or a
  // blank new publication) takes over instead of leaving the user on a
  // permanently empty editor wondering where their work went.
  useEffect(() => {
    if (!draftId || loadedFrom === draftId) return;
    if (draft) {
      setTitle(draft.title);
      setSummary(draft.summary);
      setType(draft.type as PublicationType);
      setImage(draft.image);
      setVersion(draft.version);
      setPublishedOn(draft.publishedOn);
      setPublishedBy(draft.publishedBy);
      setSource(draft.source);
      setTopics(draft.topics);
      setAuthors(draft.authors.map((a) => ({ key: a.key || nextKey(), name: a.name, role: a.role })));
      setIdentifiers(draft.identifiers.map((i) => ({ key: i.key || nextKey(), type: i.type, value: i.value })));
      setSections(
        draft.sections.map((s) => ({
          key: s.key || nextKey(),
          d: s.d,
          title: s.title,
          content: s.content,
          // Trust the format the draft was saved with; only fall back to
          // detection when it wasn't recorded, so resuming never
          // silently reinterprets AsciiDoc as Djot (or vice versa) and
          // mangles the section on the next render.
          format: isContentFormat(s.format) ? s.format : s.content ? detectContentFormat(s.content) : undefined,
          published: s.published,
        })),
      );
      setLoadedFrom(draft.id);
      setInitialized(true);
    } else {
      toast({
        title: 'That draft no longer exists',
        description: 'It may have been deleted here or created in another browser. Starting fresh instead.',
      });
      const next = new URLSearchParams(searchParams);
      next.delete('draft');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, draft, loadedFrom]);

  // Prefill from the existing index + its resolved sections once loaded.
  useEffect(() => {
    if (initialized || !isEditingExisting || draftId) return;
    if (existingIndex && existingSections !== undefined) {
      setTitle(publicationTitle(existingIndex));
      setSummary(publicationSummary(existingIndex) ?? '');
      setType(publicationType(existingIndex));
      setImage(publicationImage(existingIndex) ?? '');
      setVersion(publicationVersion(existingIndex) ?? '');
      setPublishedOn(publicationPublishedOn(existingIndex) ?? '');
      setPublishedBy(publicationPublishedBy(existingIndex) ?? '');
      setSource(publicationSource(existingIndex) ?? '');
      setTopics(getPublicationTopics(existingIndex).join(', '));
      setAuthors(
        getPublicationAuthors(existingIndex).map((a) => ({ key: nextKey(), name: a.name, role: a.role ?? '' })),
      );
      setIdentifiers(
        getPublicationIdentifiers(existingIndex).map((id) => ({ key: nextKey(), type: id.type, value: id.value })),
      );
      setSections(
        existingSections
          .filter((e) => e.kind === PUBLICATION_CONTENT_KIND)
          .map((e) => ({
            key: nextKey(),
            d: e.tags.find(([n]) => n === 'd')?.[1] ?? '',
            title: sectionTitle(e),
            content: e.content,
            format: detectContentFormat(e.content),
            published: true,
          })),
      );
      setInitialized(true);
    } else if (!indexLoading && !sectionsLoading && existingIndex === null) {
      // Finished loading and genuinely found nothing — start fresh rather
      // than spinning forever.
      setInitialized(true);
    }
  }, [existingIndex, existingSections, indexLoading, sectionsLoading, initialized, isEditingExisting, draftId]);

  useEffect(() => {
    if (sections.length > 0 && !activeSectionKey) {
      setActiveSectionKey(sections[0].key);
    }
  }, [sections, activeSectionKey]);

  // Seed the "no unsaved work" baseline with whatever the editor was
  // prefilled with, so an existing publication being edited doesn't
  // immediately read as unsaved changes the user never made.
  useEffect(() => {
    if (initialized && savedSnapshot === null) setSavedSnapshot(snapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, savedSnapshot]);

  // Warn before navigating away with unsaved work — the whole point of
  // drafts is that closing a tab shouldn't quietly destroy writing.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  if (!user) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <Card className="border-dashed">
            <CardContent className="py-12 px-8">
              <p className="mx-auto max-w-sm text-muted-foreground">
                You need to log in with a Nostr key, browser extension, or bunker to create publications.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (isEditingExisting && !initialized) {
    return (
      <Layout>
        <div className="mx-auto max-w-4xl px-4 py-8">
          <Loader2 className="mx-auto size-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  const updateSection = (key: string, patch: Partial<DraftSection>) => {
    setSections((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  };

  /**
   * Inserts media markup at the end of the active section. Goes through
   * `updateSection` so the insert lands in the section actually being
   * edited, no matter which one was active when the picker opened.
   */
  const handleInsertMedia = (markup: string) => {
    if (!activeSection) return;
    const sel = {
      value: activeSection.content,
      start: activeSection.content.length,
      end: activeSection.content.length,
    };
    const result = insertBlockAtCursor(sel, () => markup);
    updateSection(activeSection.key, { content: result.value });
  };

  const addSection = () => {
    const draft: DraftSection = { key: nextKey(), d: '', title: '', content: '', format: undefined, published: false };
    setSections((prev) => [...prev, draft]);
    setActiveSectionKey(draft.key);
  };

  const removeSection = (key: string) => {
    setSections((prev) => prev.filter((s) => s.key !== key));
    if (activeSectionKey === key) setActiveSectionKey(undefined);
  };

  const moveSection = (key: string, direction: -1 | 1) => {
    setSections((prev) => {
      const index = prev.findIndex((s) => s.key === key);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addAuthor = () => setAuthors((prev) => [...prev, { key: nextKey(), name: '', role: '' }]);
  const updateAuthor = (key: string, patch: Partial<DraftAuthor>) =>
    setAuthors((prev) => prev.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  const removeAuthor = (key: string) => setAuthors((prev) => prev.filter((a) => a.key !== key));

  const addIdentifier = () => setIdentifiers((prev) => [...prev, { key: nextKey(), type: 'isbn', value: '' }]);
  const updateIdentifier = (key: string, patch: Partial<DraftIdentifier>) =>
    setIdentifiers((prev) => prev.map((id) => (id.key === key ? { ...id, ...patch } : id)));
  const removeIdentifier = (key: string) => setIdentifiers((prev) => prev.filter((id) => id.key !== key));

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Please select an image.', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Image too large (max 10MB).', variant: 'destructive' });
      return;
    }

    try {
      const tags = await uploadFile(file);
      const url = tags[0]?.[1];
      if (url) setImage(url);
    } catch {
      toast({ title: 'Upload failed.', variant: 'destructive' });
    }
  };

  const handleSaveDraft = () => {
    if (isPublicationDraftEmpty({ title, summary, sections })) {
      toast({ title: "There's nothing to save yet.", variant: 'destructive' });
      return;
    }

    setIsSavingDraft(true);
    try {
      const savedId = drafts.save(
        {
          // Only carry a `d` when this really is a revision of something
          // already published. A new publication has none until publish
          // time, and inventing one from the title would make the draft
          // resume through `/p/<slug>/edit` and go looking on relays for
          // a publication that doesn't exist.
          ...(isEditingExisting && d ? { d } : {}),
          title,
          summary,
          type,
          image,
          version,
          publishedOn,
          publishedBy,
          source,
          topics,
          authors,
          identifiers,
          sections,
        },
        // Update the draft this editor session is already tied to, so
        // repeated saves don't stack up entries.
        loadedFrom || undefined,
      );
      setSavedSnapshot(snapshot);

      // Mark this draft as the session's source *before* putting it in
      // the URL, or the resume effect would see a `draftId` it hasn't
      // loaded and re-initialize the fields from storage, discarding any
      // keystroke landing between the save and the re-render.
      setLoadedFrom(savedId);

      // Anchor the URL to the draft id so subsequent saves update the
      // same entry instead of stacking up a new draft per click.
      if (draftId !== savedId) {
        const next = new URLSearchParams(searchParams);
        next.set('draft', savedId);
        setSearchParams(next, { replace: true });
      }

      toast({ title: 'Draft saved', description: 'Find it any time under Write → Drafts.' });
    } catch (error) {
      toast({
        title: "Couldn't save the draft",
        description:
          error instanceof Error ? error.message : 'This browser may be out of storage space.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    if (!title.trim()) {
      toast({ title: 'Please enter a title for the publication.', variant: 'destructive' });
      return;
    }
    if (!finalD) {
      toast({ title: 'Please enter a valid title.', variant: 'destructive' });
      return;
    }
    const emptySection = sections.find((s) => !s.title.trim() || !s.content.trim());
    if (emptySection) {
      toast({ title: 'Every section needs a title and content.', variant: 'destructive' });
      return;
    }
    const emptyAuthor = authors.find((a) => !a.name.trim());
    if (emptyAuthor) {
      toast({ title: 'Remove empty author entries, or fill in a name.', variant: 'destructive' });
      return;
    }
    const emptyIdentifier = identifiers.find((id) => !id.value.trim());
    if (emptyIdentifier) {
      toast({ title: 'Remove empty identifier entries, or fill in a value.', variant: 'destructive' });
      return;
    }

    setIsPublishing(true);
    try {
      // Publish each section first (kind 30041), then the index (kind
      // 30040) referencing them via `a` tags in display order — so the
      // index never points at content that doesn't exist yet.
      const publishedSections: { d: string }[] = [];
      for (const section of sections) {
        // Resolve any media still referenced locally (a `wikistar-media:`
        // placeholder) to a real Blossom URL before signing — same
        // reason as the article editor: a placeholder would ship a
        // permanently broken link.
        const pending = findPendingMediaHashes(section.content);
        let sectionContent = section.content;
        if (pending.length > 0) {
          const urlByHash = await resolveMedia.mutateAsync(pending);
          sectionContent = resolvePendingMedia(section.content, urlByHash);
        }

        // Preserve a previously-published section's `d` tag (so re-editing
        // it updates the same addressable event) rather than recomputing
        // it from the current title, which would fork a new section every
        // time the title is edited.
        const sectionD = section.published && section.d ? section.d : `${finalD}-${normalizeWikiTitle(section.title)}`;
        const tags: string[][] = [
          ['d', sectionD],
          ['title', section.title.trim()],
          ['T', normalizeWikiTitle(section.title)],
          ['alt', `Publication section: ${section.title.trim()}`],
        ];
        await publishEvent({ kind: PUBLICATION_CONTENT_KIND, content: sectionContent, tags });
        publishedSections.push({ d: sectionD });
      }

      const indexTags: string[][] = [
        ['d', finalD],
        ['title', title.trim()],
        ['T', normalizeWikiTitle(title)],
        ['type', type],
      ];
      for (const author of authors) {
        if (!author.name.trim()) continue;
        indexTags.push(author.role.trim() ? ['author', author.name.trim(), author.role.trim()] : ['author', author.name.trim()]);
        indexTags.push(['N', normalizeWikiTitle(author.name)]);
      }
      for (const id of identifiers) {
        if (!id.type.trim() || !id.value.trim()) continue;
        indexTags.push(['i', `${id.type.trim()}:${id.value.trim()}`]);
      }
      for (const topic of topics.split(',').map((t) => t.trim()).filter(Boolean)) {
        indexTags.push(['t', topic]);
      }
      if (summary.trim()) indexTags.push(['summary', summary.trim()]);
      if (image.trim()) indexTags.push(['image', image.trim()]);
      if (version.trim()) indexTags.push(['version', version.trim()]);
      if (publishedOn.trim()) indexTags.push(['published_on', publishedOn.trim()]);
      if (publishedBy.trim()) indexTags.push(['published_by', publishedBy.trim()]);
      if (source.trim()) indexTags.push(['s', source.trim()]);
      for (const s of publishedSections) {
        indexTags.push(['a', `${PUBLICATION_CONTENT_KIND}:${user.pubkey}:${s.d}`]);
      }
      indexTags.push(['alt', `Publication: ${title.trim()}`]);

      await publishEvent({ kind: PUBLICATION_INDEX_KIND, content: '', tags: indexTags });

      toast({ title: 'Publication published!' });
      if (loadedFrom) drafts.remove(loadedFrom);
      navigate(`/p/${encodeURIComponent(finalD)}/${nip19.npubEncode(user.pubkey)}`);
    } catch {
      toast({ title: 'Failed to publish. Some sections may have published — please check before retrying.', variant: 'destructive' });
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
              <BookOpen className="size-6 shrink-0" />
              <span className="truncate">{isEditingExisting ? 'Edit publication' : 'New publication'}</span>
            </h1>
            {dirty && (
              <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                <FileEdit className="size-3.5" />
                Unsaved
              </span>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {activeSection && (
              <Button type="button" variant="outline" size="icon" onClick={() => setFocusMode(true)} aria-label="Enter focus mode">
                <Maximize2 className="size-4" />
              </Button>
            )}
            <Button variant="outline" onClick={handleSaveDraft} disabled={isPublishing || isSavingDraft}>
              {isSavingDraft ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              Save draft
            </Button>
            <Button onClick={handlePublish} disabled={isPublishing}>
              {isPublishing ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              Publish
            </Button>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Publication details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <div className="shrink-0">
                <Label className="mb-1.5 block">Cover image</Label>
                <button
                  type="button"
                  onClick={() => imageFileInputRef.current?.click()}
                  disabled={isUploadingImage}
                  className="flex size-20 items-center justify-center overflow-hidden rounded-lg border border-dashed border-input bg-muted/40 text-muted-foreground hover:bg-muted"
                  title="Upload a cover image"
                >
                  {isUploadingImage ? (
                    <Loader2 className="size-5 animate-spin" />
                  ) : image ? (
                    <img src={image} alt="" className="size-full object-cover" />
                  ) : (
                    <ImagePlus className="size-6" />
                  )}
                </button>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={imageFileInputRef}
                  onChange={handleImageUpload}
                />
              </div>

              <div className="grid min-w-0 flex-1 gap-4 sm:grid-cols-[1fr_180px]">
                <div className="space-y-1.5">
                  <Label htmlFor="pub-title">Title</Label>
                  <Input id="pub-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Publication title" />
                  {finalD && <p className="text-xs text-muted-foreground">Slug: {finalD}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pub-type">Type</Label>
                  <Select value={type} onValueChange={(v) => setType(v as PublicationType)}>
                    <SelectTrigger id="pub-type" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PUBLICATION_TYPES.map((t) => (
                        <SelectItem key={t} value={t} className="capitalize">
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pub-summary">Summary (optional)</Label>
              <Textarea
                id="pub-summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="A short description shown in publication listings"
                className="resize-none"
                rows={2}
              />
            </div>

            <Accordion type="single" collapsible>
              <AccordionItem value="metadata" className="border-b-0">
                <AccordionTrigger className="text-sm text-muted-foreground hover:no-underline">
                  Authors, identifiers &amp; edition details (optional)
                </AccordionTrigger>
                <AccordionContent className="space-y-5">
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <Label>Authors</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addAuthor}>
                        <Plus className="size-3.5" />
                        Add author
                      </Button>
                    </div>
                    {authors.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        None added — the publishing account will be shown as the author.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {authors.map((author) => (
                          <div key={author.key} className="flex flex-wrap gap-2">
                            <Input
                              value={author.name}
                              onChange={(e) => updateAuthor(author.key, { name: e.target.value })}
                              placeholder="Full name"
                              className="min-w-0 flex-1"
                            />
                            <Input
                              value={author.role}
                              onChange={(e) => updateAuthor(author.key, { role: e.target.value })}
                              placeholder="Role (e.g. translator)"
                              className="min-w-0 flex-1 sm:w-40 sm:flex-none"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeAuthor(author.key)}
                              aria-label="Remove author"
                            >
                              <Trash2 className="size-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <Label>External identifiers</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addIdentifier}>
                        <Plus className="size-3.5" />
                        Add identifier
                      </Button>
                    </div>
                    {identifiers.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        Link to ISBN, OpenLibrary, Wikidata, Wikipedia, or Project Gutenberg entries.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {identifiers.map((id) => (
                          <div key={id.key} className="flex flex-wrap gap-2">
                            <Select value={id.type} onValueChange={(v) => updateIdentifier(id.key, { type: v })}>
                              <SelectTrigger className="w-full sm:w-40">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {KNOWN_IDENTIFIER_TYPES.map((t) => (
                                  <SelectItem key={t} value={t} className="capitalize">
                                    {t}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              value={id.value}
                              onChange={(e) => updateIdentifier(id.key, { value: e.target.value })}
                              placeholder={id.type === 'wikipedia' ? 'en:Article_Title' : 'value'}
                              className="min-w-0 flex-1"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeIdentifier(id.key)}
                              aria-label="Remove identifier"
                            >
                              <Trash2 className="size-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="pub-version">Edition / version</Label>
                      <Input id="pub-version" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g. 3rd edition" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="pub-published-on">Published on</Label>
                      <Input
                        id="pub-published-on"
                        value={publishedOn}
                        onChange={(e) => setPublishedOn(e.target.value)}
                        placeholder="e.g. 2003-05-13"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="pub-published-by">Published by</Label>
                      <Input
                        id="pub-published-by"
                        value={publishedBy}
                        onChange={(e) => setPublishedBy(e.target.value)}
                        placeholder="e.g. Public domain, or a publisher name"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="pub-source">Source URL</Label>
                      <Input
                        id="pub-source"
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        placeholder="https://…"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="pub-topics">Topics</Label>
                    <Input
                      id="pub-topics"
                      value={topics}
                      onChange={(e) => setTopics(e.target.value)}
                      placeholder="Comma-separated, e.g. fables, classical, literature"
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        <div className="grid min-w-0 gap-6 lg:grid-cols-[280px_1fr]">
          <div className="min-w-0">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-muted-foreground">Sections</h2>
              <Button variant="outline" size="sm" onClick={addSection}>
                <Plus className="size-4" />
                Add
              </Button>
            </div>

            {sections.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-8 px-4 text-center">
                  <p className="text-sm text-muted-foreground">No sections yet. Add your first one.</p>
                </CardContent>
              </Card>
            ) : (
              <ul className="space-y-1">
                {sections.map((section, i) => (
                  <SectionListItem
                    key={section.key}
                    section={section}
                    index={i}
                    total={sections.length}
                    active={activeSectionKey === section.key}
                    onSelect={() => setActiveSectionKey(section.key)}
                    onRemove={() => removeSection(section.key)}
                    onMove={(dir) => moveSection(section.key, dir)}
                  />
                ))}
              </ul>
            )}
          </div>

          <div className="min-w-0">
            {activeSection ? (
              <Card>
                <CardContent className="space-y-4 pt-6">
                  <div className="space-y-1.5">
                    <Label htmlFor="section-title">Section title</Label>
                    <Input
                      id="section-title"
                      value={activeSection.title}
                      onChange={(e) => updateSection(activeSection.key, { title: e.target.value })}
                      placeholder="e.g. Introduction, Chapter 1…"
                    />
                  </div>
                  <div>
                    <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                      <Label>Content</Label>
                      <div className="flex flex-wrap items-center gap-2">
                        <EditorViewToggle value={viewMode} onChange={setViewMode} />
                        <EditorFormatSelect
                          value={activeSection.format}
                          onChange={(format) => updateSection(activeSection.key, { format })}
                          className="h-8"
                        />
                      </div>
                    </div>
                    {activeSection.format === 'asciidoc' ? (
                      <AsciiDocEditor
                        value={activeSection.content}
                        onChange={(content) => updateSection(activeSection.key, { content })}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        onRequestMedia={() => setMediaOpen(true)}
                        onToggleFocus={() => setFocusMode((f) => !f)}
                        onSave={handleSaveDraft}
                        placeholder={editorPlaceholder(activeSection.format, 'section')}
                      />
                    ) : (
                      <DjotEditor
                        value={activeSection.content}
                        onChange={(content) => updateSection(activeSection.key, { content })}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        onRequestMedia={() => setMediaOpen(true)}
                        onToggleFocus={() => setFocusMode((f) => !f)}
                        onSave={handleSaveDraft}
                        placeholder={editorPlaceholder(activeSection.format, 'section')}
                      />
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-12 px-8 text-center">
                  <p className="mx-auto max-w-sm text-muted-foreground">
                    Select a section on the left to edit it, or add a new one.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Document stats strip — same shape as the article footer, not
            only visible in focus mode. */}
        <footer className="mt-3 flex justify-end gap-4 border-t border-border pt-2 text-xs text-muted-foreground">
          {activeSection && (
            <>
              <span>{stats.lines} lines</span>
              <span>{stats.words} words</span>
              <span title="Raw source, markup included">{stats.charsWithMarkup} chars</span>
              <span title="Markup and URLs stripped, roughly readable prose">{stats.charsWithoutMarkup} chars without markup</span>
            </>
          )}
        </footer>

        {/* Escape hatch out of the editor, matching the article editor.
            An existing publication goes back to its own page; a brand-new
            one has no page to return to, so it goes home. */}
        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link
            to={isEditingExisting && d ? `/p/${encodeURIComponent(d)}/${nip19.npubEncode(user.pubkey)}` : '/'}
            className="underline underline-offset-2 hover:text-foreground"
          >
            Cancel and go back
          </Link>
        </p>

        <MediaPickerDialog
          open={mediaOpen}
          onOpenChange={setMediaOpen}
          format={activeSection?.format === 'asciidoc' ? 'asciidoc' : 'djot'}
          onInsert={handleInsertMedia}
        />

        {focusMode && activeSection && (
          <FocusModeView
            title={activeSection.title.trim() || 'Untitled section'}
            statusBadge={<SaveStatusBadge state={saveState} onSave={handleSaveDraft} />}
            headerControls={
              <FocusSectionNav
                sections={sections.map((s) => ({ key: s.key, title: s.title }))}
                activeKey={activeSectionKey}
                onSelect={setActiveSectionKey}
              />
            }
            footer={
              <>
                <span>{stats.lines} lines</span>
                <span>{stats.words} words</span>
                <span title="Character count of the raw source, markup included">
                  {stats.charsWithMarkup} chars
                </span>
                <span title="Character count with markup delimiters and URLs stripped — roughly the readable text">
                  {stats.charsWithoutMarkup} chars without markup
                </span>
              </>
            }
            onClose={() => setFocusMode(false)}
          >
            <div className="mb-2 flex grow-0 items-center justify-between gap-2">
              <Input
                value={activeSection.title}
                onChange={(e) => updateSection(activeSection.key, { title: e.target.value })}
                placeholder="Section title"
                className="h-8"
              />
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <EditorViewToggle value={viewMode} onChange={setViewMode} />
                <EditorFormatSelect
                  value={activeSection.format}
                  onChange={(format) => updateSection(activeSection.key, { format })}
                  className="h-8"
                />
              </div>
            </div>
            {activeSection.format === 'asciidoc' ? (
              <AsciiDocEditor
                value={activeSection.content}
                onChange={(content) => updateSection(activeSection.key, { content })}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onRequestMedia={() => setMediaOpen(true)}
                onToggleFocus={() => setFocusMode((f) => !f)}
                onSave={handleSaveDraft}
                placeholder={editorPlaceholder(activeSection.format, 'section-focus')}
                fullHeight
              />
            ) : (
              <DjotEditor
                value={activeSection.content}
                onChange={(content) => updateSection(activeSection.key, { content })}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onRequestMedia={() => setMediaOpen(true)}
                onToggleFocus={() => setFocusMode((f) => !f)}
                onSave={handleSaveDraft}
                placeholder={editorPlaceholder(activeSection.format, 'section-focus')}
                fullHeight
              />
            )}
          </FocusModeView>
        )}
      </div>
    </Layout>
  );
}

function SectionListItem({
  section,
  index,
  total,
  active,
  onSelect,
  onRemove,
  onMove,
}: {
  section: DraftSection;
  index: number;
  total: number;
  active: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
}) {
  return (
    <li
      className={`group flex items-center gap-1 rounded-md px-2 py-1.5 ${
        active ? 'bg-accent' : 'hover:bg-accent/50'
      }`}
    >
      <GripVertical className="size-4 shrink-0 text-muted-foreground/50" />
      <button type="button" onClick={onSelect} className="min-w-0 flex-1 truncate text-left text-sm">
        <span className="mr-1.5 text-xs text-muted-foreground">{index + 1}.</span>
        {section.title || 'Untitled section'}
      </button>
      <div className="flex shrink-0 items-center opacity-0 group-hover:opacity-100">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-6"
          disabled={index === 0}
          onClick={() => onMove(-1)}
          aria-label="Move up"
        >
          <ChevronUp className="size-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-6"
          disabled={index === total - 1}
          onClick={() => onMove(1)}
          aria-label="Move down"
        >
          <ChevronDown className="size-3.5" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="size-6 text-destructive" onClick={onRemove} aria-label="Remove section">
          <Trash2 className="size-3.5" />
        </Button>
      </div>
    </li>
  );
}
